import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { CampaignsApi, campaignKeys } from "@/api/perkpoint/campaigns";
import {
  campaigns,
  type CampaignRow,
  type CampaignStatus,
  type CampaignType,
} from "./Campaigns";
import { getSavedCampaignRows } from "./campaign-builder/model";
import { hasResults } from "./campaign-performance/performance";
import { useActiveProgramId } from "../programs/program-scope";

const STEPS = [
  "Audience",
  "Schedule",
  "Offer",
  "Message",
  "Guardrails",
  "Review",
] as const;

function StatusBadge({ status }: { status: CampaignStatus }) {
  const styles: Record<CampaignStatus, string> = {
    Ongoing: "bg-green-50 text-green-700",
    Scheduled: "bg-sky-50 text-sky-700",
    "Pending approval": "bg-amber-50 text-amber-700",
    Ended: "bg-slate-100 text-slate-600",
    Draft: "bg-slate-100 text-slate-700",
  };

  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        styles[status],
      )}
    >
      {status}
    </span>
  );
}

function TypeBadge({ type }: { type: CampaignType }) {
  return (
    <span className="inline-flex rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
      {type}
    </span>
  );
}

function SectionBadge({ step }: { step: number }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-semibold text-white">
      {step}
    </span>
  );
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

export default function CampaignDetails() {
  const { id } = useParams();
  const { programId } = useActiveProgramId();
  useQuery({
    queryKey: campaignKeys.detail(programId ?? "", id ?? ""),
    queryFn: () => CampaignsApi.get(programId!, id!),
    enabled: Boolean(programId && id),
  });
  const campaign: CampaignRow | undefined = useMemo(
    () =>
      [...getSavedCampaignRows(), ...campaigns].find((item) => item.id === id),
    [id],
  );

  const [pointsOfferOn, setPointsOfferOn] = useState(true);
  const [scheduleMode, setScheduleMode] = useState<
    "once" | "repeating" | "event"
  >("once");
  const [messageTab, setMessageTab] = useState<"whatsapp" | "email">(
    "whatsapp",
  );
  const [activeStep, setActiveStep] = useState(1);

  const audienceRef = useRef<HTMLDivElement>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const offerRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const guardrailsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  const sectionRefs = [
    audienceRef,
    scheduleRef,
    offerRef,
    messageRef,
    guardrailsRef,
    reviewRef,
  ];

  const scrollToStep = (stepNumber: number) => {
    setActiveStep(stepNumber);
    const target = sectionRefs[stepNumber - 1]?.current;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!campaign) {
    return (
      <div className="space-y-4 pb-2">
        <p className="text-sm text-muted-foreground">Campaign not found.</p>
        <Button asChild variant="outline">
          <Link to="/perkpoint/rewards/campaigns">Back to campaigns</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-2">
      {/* Breadcrumb + actions */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Campaigns <span className="mx-1 text-slate-300">/</span>{" "}
            <span className="text-slate-700">{campaign.name}</span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {campaign.name}
            </h1>
            <StatusBadge status={campaign.status} />
            <TypeBadge type={campaign.type} />
          </div>
          <p className="mt-2 max-w-3xl text-muted-foreground">
            {campaign.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="bg-white">
            Send test to myself
          </Button>
          {hasResults(campaign.status) ? (
            <Button asChild variant="outline" className="bg-white">
              <Link to={`/perkpoint/rewards/campaigns/${campaign.id}/performance`}>
                View performance
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" className="bg-white">
            <Link to={`/perkpoint/rewards/campaigns/${campaign.id}/edit`}>
              Edit campaign
            </Link>
          </Button>
          <Button
            type="button"
            className="bg-green-600 text-white hover:bg-green-700"
          >
            Submit for approval
          </Button>
        </div>
      </div>

      {/* Stepper */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="flex min-w-max items-center gap-2">
          {STEPS.map((step, index) => {
            const stepNumber = index + 1;
            const isActive = activeStep === stepNumber;
            return (
              <button
                key={step}
                type="button"
                onClick={() => scrollToStep(stepNumber)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-green-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                )}
              >
                <span
                  className={cn(
                    "inline-flex h-5 w-5 items-center justify-center rounded-full text-xs",
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-white text-slate-600",
                  )}
                >
                  {stepNumber}
                </span>
                {step}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {/* Basic campaign details */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-slate-900">
                Basic campaign details
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">ID</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.code}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.type}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.status}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Channels</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.channels.join(", ")}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Bonus points</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.bonusPoints}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Date range</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.startDate} – {campaign.endDate}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">
                    Members reached
                  </p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.membersReached}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Points issued</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.pointsIssued}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-muted-foreground">Messages</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {campaign.messages}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audience */}
          <div ref={audienceRef} className="scroll-mt-24">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <SectionBadge step={1} />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Audience
                  </h2>
                </div>
                <button
                  type="button"
                  className="text-sm font-medium text-green-700 hover:text-green-800"
                >
                  Manage segments
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    Include members in
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-sm text-slate-700">
                      At risk 9,940
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-sm text-slate-700">
                      Hibernating 41,230
                    </span>
                    <Button type="button" variant="outline" size="sm">
                      + Segment
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    Exclude
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-sm text-red-700">
                      Tier: Platinum
                    </span>
                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-sm text-red-700">
                      Tier: Prestige
                    </span>
                    <Button type="button" variant="outline" size="sm">
                      + Exclusion
                    </Button>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-200">
                {[
                  ["Members matched", "51,170"],
                  ["- Excluded tiers", "1,060"],
                  ["- No consent on any channel", "8,410"],
                  ["- Control group, 10% held out", "4,170"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-sm last:border-b-0"
                  >
                    <span className="text-slate-600">{label}</span>
                    <span className="font-medium text-slate-800">{value}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between bg-green-50 px-4 py-2.5 text-sm">
                  <span className="font-semibold text-slate-900">
                    Will be messaged
                  </span>
                  <span className="font-bold text-slate-900">37,530</span>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Schedule */}
          <div ref={scheduleRef} className="scroll-mt-24">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex items-center gap-2">
                <SectionBadge step={2} />
                <h2 className="text-lg font-semibold text-slate-900">
                  Schedule
                </h2>
              </div>

              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ["once", "Once, at a set time"],
                    ["repeating", "On a repeating schedule"],
                    ["event", "When an event happens"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setScheduleMode(id)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                      scheduleMode === id
                        ? "bg-green-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <DetailField label="Date and time">
                  <Input
                    defaultValue="15 Nov 2026, 10:00"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Time zone">
                  <Select defaultValue="dubai">
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dubai">
                        Program · Asia/Dubai
                      </SelectItem>
                      <SelectItem value="kolkata">
                        Program · Asia/Kolkata
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </DetailField>
                <DetailField label="Campaign ends">
                  <Input
                    defaultValue="31 Dec 2026"
                    className="h-10 bg-white"
                  />
                </DetailField>
              </div>
              <p className="text-sm text-muted-foreground">
                Event-triggered campaigns listen for events like bookings or
                tier upgrades and send when conditions match.
              </p>
            </CardContent>
          </Card>
          </div>

          {/* Offer */}
          <div ref={offerRef} className="scroll-mt-24">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <SectionBadge step={3} />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Offer
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Points offer on
                  </span>
                  <Switch
                    checked={pointsOfferOn}
                    onCheckedChange={setPointsOfferOn}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailField label="Offer type">
                  <Select defaultValue="multiply">
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multiply">Multiply points</SelectItem>
                      <SelectItem value="fixed">Fixed bonus</SelectItem>
                    </SelectContent>
                  </Select>
                </DetailField>
                <DetailField label="Multiplier">
                  <Input
                    defaultValue="2x on base earn"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="On event">
                  <Select defaultValue="booking">
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booking">booking.completed</SelectItem>
                      <SelectItem value="purchase">purchase.completed</SelectItem>
                    </SelectContent>
                  </Select>
                </DetailField>
                <DetailField label="Offer valid">
                  <Input
                    defaultValue="15 Nov – 31 Dec 2026"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Cap per member">
                  <Input
                    defaultValue="2,000 bonus points"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Budget">
                  <Input
                    defaultValue="500,000 points (AED 5,000)"
                    className="h-10 bg-white"
                  />
                </DetailField>
              </div>

              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-slate-700">
                Creates rule{" "}
                <span className="font-semibold">
                  &apos;{campaign.name} 2x&apos;
                </span>{" "}
                at priority 42 in group campaign, limited to this audience. It
                shows up in the rules engine and pauses itself when the budget
                runs out.
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Message */}
          <div ref={messageRef} className="scroll-mt-24">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <SectionBadge step={4} />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Message
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Fallback runs in order if a channel has no consent or fails.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
                  1 WhatsApp
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-700">
                  2 Email
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-500">
                  SMS - off
                </span>
                <Button type="button" variant="outline" size="sm">
                  Change order
                </Button>
              </div>

              <div className="flex gap-4 border-b border-slate-200">
                {(["whatsapp", "email"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setMessageTab(tab)}
                    className={cn(
                      "border-b-2 pb-2 text-sm font-medium capitalize",
                      messageTab === tab
                        ? "border-green-600 text-green-700"
                        : "border-transparent text-slate-500",
                    )}
                  >
                    {tab === "whatsapp" ? "WhatsApp" : "Email"}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-sm leading-relaxed text-slate-800">
                      Hi Dummy Member, we&apos;ve missed you at Dummy Program!
                      Book any stay before 31 Dec and earn double points. You
                      have 4,320 points waiting.
                    </p>
                    <Button
                      type="button"
                      className="mt-4 w-full bg-green-600 text-white hover:bg-green-700"
                    >
                      Book now
                    </Button>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Template winback_q4_v1 ·{" "}
                    <span className="font-medium text-green-700">
                      Approved by Meta
                    </span>
                  </p>
                </div>

                <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-semibold text-slate-900">
                    Merge fields
                  </p>
                  {[
                    [
                      "first_name",
                      "member.first_name — fallback \"there\"",
                    ],
                    ["points_balance", "member.available_points"],
                    ["offer_end_date", "offer.valid_to"],
                    ["book_link", "Dummy Program deep link with tracking"],
                  ].map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <p className="font-mono text-xs text-green-700">{key}</p>
                      <p className="text-slate-600">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Guardrails */}
          <div ref={guardrailsRef} className="scroll-mt-24">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex items-center gap-2">
                <SectionBadge step={5} />
                <h2 className="text-lg font-semibold text-slate-900">
                  Guardrails and tracking
                </h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailField label="Frequency cap">
                  <Input
                    defaultValue="Program default · 3 per 7 days"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Quiet hours">
                  <Input
                    defaultValue="21:00 – 09:00"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Control group">
                  <Input
                    defaultValue="10% held out"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Success event">
                  <Select defaultValue="booking">
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booking">booking.completed</SelectItem>
                      <SelectItem value="redeem">points.redeemed</SelectItem>
                    </SelectContent>
                  </Select>
                </DetailField>
                <DetailField label="Attribution window">
                  <Input
                    defaultValue="7 days after message"
                    className="h-10 bg-white"
                  />
                </DetailField>
                <DetailField label="Approver">
                  <Select defaultValue="dummy">
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dummy">
                        Dummy Member · Program admin
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </DetailField>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>

        {/* Sidebar */}
        <div
          ref={reviewRef}
          className="scroll-mt-24 space-y-4 xl:sticky xl:top-4 xl:self-start"
        >
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-3 p-5">
              <h3 className="text-base font-semibold text-slate-900">
                Before you send
              </h3>
              <p className="text-xs text-muted-foreground">Live estimate</p>
              <div className="space-y-2 text-sm">
                {[
                  ["Members messaged", "37,530"],
                  ["Via WhatsApp", "29,840"],
                  ["Via email (fallback)", "7,690"],
                  ["Held out as control", "4,170"],
                  ["Maximum points cost", "500,000 · AED 5,000"],
                  ["Last win-back (Q2)", "4.6% converted · +2.9 pts lift"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-start justify-between gap-3"
                  >
                    <span className="text-muted-foreground">{label}</span>
                    <span className="text-right font-medium text-slate-800">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-slate-900">Checks</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  <p className="text-slate-700">
                    WhatsApp template approved; email has an unsubscribe link.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  <p className="text-slate-700">
                    Offer rule is valid and does not overlap another live offer.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  <p className="text-slate-700">
                    Send time is outside quiet hours.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                  <p className="text-slate-700">
                    Budget is above 100,000 points, so a second admin must
                    approve.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                className="w-full bg-green-600 text-white hover:bg-green-700"
              >
                Submit for approval
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
